import { Inline, Button, Text, Field, IconButton, Stack, Box, Card, CardContent, CardHeader, CardList, CardListRow, CardListItem, CardListText, ConfirmationModal, List, ListItem, Grid } from "@digitalreality/ui";
import { useEffect, useState } from "react";
import React from "react";
import { useAppState } from "../../StateStore/Store";
import { RenewIcon, ViewIcon, ViewOffIcon } from "@digitalreality/ui-icons";
import { RestApiResponse } from "../../Models/RestApiResponse";
import { isValidLicenseCode, redeemLicenseForProjectAsync, updateLicenseForProjectAsync } from "../../Services/LicensesService";
import dayjs from "dayjs";
import { LicenseCodes, LicenseCodesAddOns } from "../../Helpers/Constants";
import { License as LicenseModel } from "../../Models/License";
import { LicenseGroup } from "../../Models/LicenseGroup";
import { useForm } from "react-hook-form";
import { notAuthorised } from "../../StateStore/UserAuth/UserAuthActions";
import { getLicenses } from "../../StateStore/ProjectModal/ProjectModalActionMiddleware";
import { pangeaUIColors } from "../../Helpers/Colors";
import { updateProjectsList } from "../../StateStore/ProjectList/ProjectListActions";
import { ProjectSummary } from "../../Models/ProjectSummary";
import { Trans, useTranslation } from "react-i18next";
import Divider from '@mui/material/Divider';

export default function License({ onSubmit, formRef, editMode = true }: any) {
  const { t } = useTranslation();
  const { state, dispatch } = useAppState();

  const { setValue, handleSubmit } = useForm({
    mode: "onBlur",

    defaultValues: state.projectModal.project,
  });

  const [licenseKey, setLicenseKey] = useState("");
  const [showAddLicenseKeyField, setShowAddLicenseKeyField] = useState(false);
  const [licenseKeyInvalid, setLicenseKeyInvalid] = useState(false);
  const [showLicenseKeyConfirmModal, setShowLicenseKeyConfirmModal] =
    useState(false);
  const [showLicenseKeyList, setShowLicenseKeyList] = useState<string[]>([]);
  const [licenseActivateError, setLicenseActivateError] = useState("");
  const [createProjectLicenseKey, setCreateProjectLicenseKey] = useState(false);
  const [failedUpdateList, setFailedUpdateList] = useState<string[]>([]);

  useEffect(() => {
    if (state.projectModal.licenses && licenseKey) {
      const project: ProjectSummary = {
        id: state.projectModal.project?.dealerProjectId as number,
        licenses: state.projectModal.licenses[licenseKey],
      } as ProjectSummary;
      dispatch(updateProjectsList(project));
    }
  }, [state.projectModal.licenses]);

  const validateCode = (value: string) => {
    return isValidLicenseCode(
      state.projectModal.project ? state.projectModal.project?.projectName : "",
      value
    ).then((response: RestApiResponse) => {
      if (!response.success) {
        setLicenseKeyInvalid(true);
        return false;
      } else {
        setLicenseKeyInvalid(false);
        setLicenseActivateError("");
        setShowLicenseKeyConfirmModal(true);
        return true;
      }
    });
  };

  const activateLicense = () => {
    const projectName = state.projectModal.project?.projectName as string;
    if (editMode) {
      redeemLicenseForProjectAsync(projectName, licenseKey).then(
        (response: RestApiResponse) => {
          if (response.statusCode === 401) {
            dispatch(notAuthorised());
          }
          if (response.success) {
            getLicenses(projectName)(dispatch);
            setShowAddLicenseKeyField(false);
            setShowLicenseKeyConfirmModal(false);
          } else {
            setLicenseActivateError(t("Texts.LicenseActivationError"));
          }
        }
      );
    } else {
      setValue("licenseKey", licenseKey);
      setCreateProjectLicenseKey(true);
      setShowAddLicenseKeyField(false);
      setShowLicenseKeyConfirmModal(false);
    }
  };

  const updateLicense = (licenseKey: string) => {
    const projectName = state.projectModal.project?.projectName as string;
    updateLicenseForProjectAsync(projectName, licenseKey).then(
      (response: RestApiResponse) => {
        if (response.statusCode === 401) {
          dispatch(notAuthorised());
        }
        if (response.success) {
          setLicenseKey(licenseKey);
          getLicenses(projectName)(dispatch);
        } else {
          // show error
          setFailedUpdateList((prevstate) => [...prevstate, licenseKey]);
        }
      }
    );
  };

  const setLicenseText = (licenses: LicenseModel[]) => {
    var licenseCodes: string[] = [];
    var addOns: string[] = [];

    licenses.map((license) => {
      if (Object.keys(LicenseCodes).includes(license.code)) {
        if (Object.keys(LicenseCodesAddOns).includes(license.code)) {
          addOns.push(license.code);
        } else {
          licenseCodes.push(license.code);
        }
      }
    });

    return (
      <>
        <Text>{licenseCodes.join(", ")}</Text>
        <Text variant="caption" color="text.disabled">
          {addOns.join(", ")}
        </Text>
      </>
    );
  };

  const toggleShowLicenseKey = (licenseKey: string) => {
    const toggledState = showLicenseKeyList.slice();
    const index = toggledState.indexOf(licenseKey);
    index === -1
      ? toggledState.push(licenseKey)
      : toggledState.splice(index, 1);

    setShowLicenseKeyList(toggledState);
  };

  const licenseCodeField = (
    <Inline gap={2.5} justifyItems="center" alignItems="baseline">
      <Field
        sx={{ width: "350px" }}
        error={!!licenseKeyInvalid}
        value={licenseKey}
        onChange={(element) => {
          setLicenseKey(element.target.value);
        }}
        helperText={licenseKeyInvalid ? t("Texts.LicenseKeyNotValidError") : ""}
      />

      <Button
        variant="accent"
        onClick={() => {
          validateCode(licenseKey);
        }}
      >
        {t("Texts.Activate")}
      </Button>
    </Inline>
  );

  const noLicensesField = (
    <CardListItem>
      <CardListText>{t("Texts.NoLicense")}</CardListText>
    </CardListItem>
  );

    const createProjectLicenseField = (
        <CardListItem color="green">
            <CardListText>
                <Trans i18nKey="Texts.LicenseKeySuccessValidation">
                    License key {{ licenseKey }} was validated and will be added with
                    project create.
                </Trans>
            </CardListText>
        </CardListItem>
    );

    return (
        <form
            id="licenseForm"
            onSubmit={handleSubmit(onSubmit)}
            ref={formRef}
            autoComplete="off">
            <Stack gap={5}>
                <Card sx={{ height: "480px", p: 0}}>
                    <CardHeader
                        title="Licenses" // This places the title on the left side
                        action={editMode && (
                            <IconButton
                                title={t("Texts.UpdateLicenses")}
                                onClick={() => {Object.keys(state.projectModal.licenses ?? []).forEach(function (key) {updateLicense(key);});}}>
                                <RenewIcon />
                            </IconButton>
                        )}/>
                    <Divider />
                    <CardContent sx={{ height: "490px", maxHeight: "490px", overflow: 'auto'}}>
                        {!state.projectModal.licenses || (Object.keys(state.projectModal.licenses).length === 0 && (
                            <List
                                sx={{pl: "0"}}>
                                <ListItem sx={{ height: "80px", padding: '0'}}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={3} style={{ paddingTop: 0 }}>
                                            {(createProjectLicenseKey && createProjectLicenseField) || noLicensesField}
                                        </Grid>
                                    </Grid>
                                </ListItem>
                            </List>
                        ))}
                        {Object.keys(state.projectModal.licenses ?? []).map((key) => {
                            const licenseKeyEntitlements = (state.projectModal.licenses as LicenseGroup)[key];
                            return (
                                <List
                                    sx={{pl: "14px"}}
                                    key={key}>
                                    <ListItem sx={{ height: "80px", padding: '5'}}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={4} style={{ paddingTop: 0 }}>
                                                {setLicenseText(licenseKeyEntitlements)}
                                            </Grid>
                                            <Grid item xs={4} style={{ paddingTop: 0 }}>
                                                <Text color={dayjs(licenseKeyEntitlements[0].enddate).isBefore(dayjs(), "day") ? "red" : "green"}>
                                                    {t("Texts.LicensePeriod")}:
                                                    {dayjs(licenseKeyEntitlements[0].startdate).format("DD MMM YYYY")}{" "}-{" "}{dayjs(licenseKeyEntitlements[0].enddate).format("DD MMM YYYY")}
                                                </Text>
                                            </Grid>
                                        <Grid item xs={1} style={{ paddingTop: 0 }}>
                                            <IconButton
                                                onClick={() => {toggleShowLicenseKey(key);}}>
                                                {showLicenseKeyList.includes(key) ? (<ViewIcon />) : (<ViewOffIcon />)}
                                            </IconButton>
                                        </Grid>
                                        <Grid item xs={3} style={{ paddingTop: 0 }}>
                                            {(showLicenseKeyList.includes(key) && key) || "xxxxx-xxxxx-xxxxx-xxxxx-xxxxxx"}
                                        </Grid>
                                        <Grid item xs={2} style={{ paddingTop: 0 }}>
                                            <Text>
                                                {failedUpdateList.includes(key) && t("Texts.UpdateFailed")}
                                            </Text>
                                        </Grid>
                                        </Grid>
                                    </ListItem>
                                    <Divider />
                                </List>
                            );
                        })}
                    </CardContent>
                </Card>

                <Inline justifyContent="flex-end" alignItems="baseline" gap={5}>
                    {showAddLicenseKeyField && licenseCodeField}
                    {!showAddLicenseKeyField && (
                        <Button
                            variant="accent"
                            onClick={() => {
                                setShowAddLicenseKeyField(true);
                                setLicenseKey("");
                                if (!editMode) {
                                    setCreateProjectLicenseKey(false);
                                }
                            }}>
                            {t("Texts.AddLicenseCode")}
                        </Button>
                    )}
                </Inline>
            </Stack>
            <ConfirmationModal
                title={t("Texts.ConfirmActivateLicense")}
                size="small"
                open={showLicenseKeyConfirmModal}
                cancelButtonText={t("Texts.Cancel")}
                confirmButtonText={t("Texts.Activate")}
                onCancel={() => setShowLicenseKeyConfirmModal(false)}
                onConfirm={activateLicense}>
                {t("Texts.ActionNotRevertable")}
                <Text color="red" mt={5}>
                    {licenseActivateError}
                </Text>
            </ConfirmationModal>
        </form>
    );
}
